import { FC, ReactNode } from 'react';

import { FilterValues } from 'src/types/CataloguePage.types';

import { FilterOption, FilterParams, PlpFiltersConditionalProps } from './PlpFilters.type';
import { AvailableFilterInput } from './filters/AvailableFilter/AvailableFilterInput';
import { AvailableFilterLink } from './filters/AvailableFilter/AvailableFilterLink';
import { FilterANCAP } from './filters/FilterANCAP';
import { FilterBodyType } from './filters/FilterBodyType';
import { FilterCO2 } from './filters/FilterCO2';
import { FilterColour } from './filters/FilterColour';
import { FilterDoors } from './filters/FilterDoors';
import { FilterDriveType } from './filters/FilterDriveType';
import { FilterFuelType } from './filters/FilterFuelType';
import { FilterHighlightedFeatures } from './filters/FilterHighlightedFeatures';
import { FilterKilometers } from './filters/FilterKilometers';
import { FilterMakeModel } from './filters/FilterMakeModel';
import { FilterPrice } from './filters/FilterPrice';
import { FilterSeats } from './filters/FilterSeats';
import { FilterTowCapacityBraked } from './filters/FilterTowCapacityBraked';
import { FilterTransmission } from './filters/FilterTransmission';
import { FilterYear } from './filters/FilterYear';
import { extractFilterOptions } from './plpFilterOptions.helper';

type BaseProps = {
  filters: ReturnType<typeof extractFilterOptions>;
  filterParam: string;
  currentFilters: FilterOption[];
  driveTrainHelper?: ReactNode;
  params?: FilterParams;
};

type Props = BaseProps & PlpFiltersConditionalProps;

export const PlpFilters: FC<Props> = ({ filters, filterParam, currentFilters, driveTrainHelper, params, ...props }) => {
  const {
    makeModelFilters,
    bodyTypeFilters,
    priceFilters,
    yearFilters,
    kilometersFilters,
    fuelTypeFilters,
    highlightedFeaturesFilters,
    transmissionFilters,
    driveTypeFilters,
    seatsFilters,
    doorsFilters,
    colourFilters,
    ancapFilters,
    co2Filters,
    towCapBrakedFilters,
  } = filters;
  return (
    <div data-testid="plp-filters">
      <FilterMakeModel
        options={makeModelFilters.options}
        currentFilters={currentFilters}
        index={0}
        showNotificationBadge={makeModelFilters.hasFilter}
        {...props}
      />
      <FilterBodyType
        options={bodyTypeFilters.options}
        currentFilters={currentFilters}
        index={1}
        showNotificationBadge={bodyTypeFilters.hasFilter}
        {...props}
      />
      <FilterPrice
        options={priceFilters.options}
        currentFilters={currentFilters}
        index={2}
        showNotificationBadge={priceFilters.hasFilter}
        {...props}
      />
      <FilterYear
        options={yearFilters.options}
        index={3}
        showNotificationBadge={yearFilters.hasFilter}
        minValue={yearFilters.minValue}
        maxValue={yearFilters.maxValue}
        {...props}
      />
      <FilterKilometers
        options={kilometersFilters.options}
        minValue={kilometersFilters.minValue}
        maxValue={kilometersFilters.maxValue}
        index={4}
        showNotificationBadge={kilometersFilters.hasFilter}
        {...props}
      />
      <FilterFuelType
        options={fuelTypeFilters.options}
        currentFilters={currentFilters}
        index={5}
        showNotificationBadge={fuelTypeFilters.hasFilter}
        {...props}
      />
      <FilterHighlightedFeatures
        options={highlightedFeaturesFilters.options}
        currentFilters={currentFilters}
        index={6}
        showNotificationBadge={highlightedFeaturesFilters.hasFilter}
        {...props}
      />
      <FilterTransmission
        options={transmissionFilters.options}
        currentFilters={currentFilters}
        index={7}
        showNotificationBadge={transmissionFilters.hasFilter}
        {...props}
      />
      <FilterDriveType
        options={driveTypeFilters.options}
        currentFilters={currentFilters}
        driveTrainHelper={driveTrainHelper}
        index={8}
        showNotificationBadge={driveTypeFilters.hasFilter}
        {...props}
      />
      <FilterSeats
        options={seatsFilters.options}
        currentFilters={currentFilters}
        index={9}
        showNotificationBadge={seatsFilters.hasFilter}
        {...props}
      />
      <FilterDoors
        options={doorsFilters.options}
        currentFilters={currentFilters}
        index={10}
        showNotificationBadge={doorsFilters.hasFilter}
        {...props}
      />
      <FilterColour
        options={colourFilters.options}
        currentFilters={currentFilters}
        index={11}
        showNotificationBadge={colourFilters.hasFilter}
        {...props}
      />
      <FilterANCAP
        options={ancapFilters.options}
        value={ancapFilters.value}
        index={12}
        showNotificationBadge={ancapFilters.hasFilter}
        {...props}
      />
      <FilterCO2
        options={co2Filters.options}
        value={co2Filters.value}
        index={13}
        showNotificationBadge={co2Filters.hasFilter}
        {...props}
      />
      <FilterTowCapacityBraked
        options={towCapBrakedFilters.options}
        index={14}
        showNotificationBadge={towCapBrakedFilters.hasFilter}
        minValue={towCapBrakedFilters.minValue}
        maxValue={towCapBrakedFilters.maxValue}
        {...props}
      />
      {props.type === 'modal' ? (
        <AvailableFilterInput
          onChange={props.onSelectFilter}
          value={currentFilters.find((filter) => filter.filter === FilterValues.State)?.value ?? ''}
        />
      ) : (
        <AvailableFilterLink params={{ filter: filterParam, sort: params?.sort }} />
      )}
    </div>
  );
};
